import type { Organization } from '@/shared/types/userTypes';
import type { Customer } from '@/shared/types/customerTypes';
import type { Product } from '@/shared/types/productTypes';
import type { UserTerminal } from '@/shared/types/userTerminalTypes';

// Service Line Type
export enum ServiceLineType {
  PRIMARY = 'Primary',
  BACKUP = 'Backup',
}

// Service Line Status
export enum ServiceLineStatus {
  ACTIVE = 'Active',
  EXHAUSTED = 'Exhausted',
  SUSPENDED = 'Suspended',
  PAUSED = 'Paused',
  NO_UT = 'No_UT',
  OFFLINE = 'Offline'
}

// Service Line Overage Status
export enum OverageStatus {
  ACTIVE = 'Active',
  EXPIRED = 'Expired',
  NO_OVERAGE = 'No Overage'
}

/**
 * Interface for service line overage information
 */
export interface OverageInfo {
  status: OverageStatus;
  dataAmount: number;
  info: string;
  shortInfo: string;
}

export type OverageMode = 'LIMITED' | 'UNLIMITED';

export interface OverageData {
  mode: OverageMode | null;
  dataAmount: number | null;
  dataUnit: 'GB' | null;
  overageEndDate: string | null;
}

export interface DailyDataUsage {
  date: string;
  totalDataUsageGB: number | 0;
  standardGB: number | 0;
}

export interface DataUsage {
  startDate: string;
  endDate: string;
  totalDataUsageGB: number | 0;
  totalStandardGB: number | 0;
  cappedForecastGB: number | 0;
  uncappedForecastGB: number | 0;
  contractedDatapoolGB: number | 0;
  basePlanGB: number | 0;
  dailyDataUsages: DailyDataUsage[] | null;
}

export interface DataUsagesResponse {
  data: {
    allTimeDataUsageGB: number | 0;
    results: DataUsage[] | null;
  }
}

export interface ServiceLine {
  serviceLineNumber: string;
  serviceLineName: string | null;
  startDate: string | null;
  endDate: string | null;
  pausedAt?: string | null;
  resumesAt?: string | null;
  status: ServiceLineStatus;
  type: ServiceLineType | null;
  publicIp: boolean | null;
  notes: string | null;
  overageAllowed: boolean | true;
  updateAllowed: boolean | true;
  dataUsages: DataUsage[] | null;
  organization: Organization | null;
  customer: Customer | null;
  product: Product | null;
  overage: OverageData | null;
  terminals: UserTerminal[] | null;
}

export interface ExhaustionInfo {
  exhaustionDate: Date | null;
  daysRemaining: number | null;
  status: 'safe' | 'warning' | 'critical' | 'exceeded';
  message: string;
  planAdequacy: 'sufficient' | 'insufficient' | 'unknown';
  monthlyProjection: number | null;
  remainingDaysInMonth: number | null;
}

// API Response Types
export interface CreateServiceLineRequest {
  orbiusProductReferenceId: string;
  type: ServiceLineType;
  serviceLineName: string;
  customerId: string | undefined;
  terminalId: string | undefined;
}

// Service Line Info
export interface ServiceLineInfo {
  serviceLineNumber: string;
  serviceLineName: string;
  activationDate: string;
  deactivationDate: string;
  status: ServiceLineStatus;
  productName: string;
  terminals: string[];
  activeAlerts: string[];
  basePlan: number;
  overageInfo: OverageInfo | null;
  customerName: string | null;
  previousMonthDataUsage: number;
  currentMonthBasePlanUsage: number;
  currentMonthOverageUsage: number | null;
  currentMonthTotalUsage: number;
  currentMonthUsagePercentage: number | null;
  currentMonthCappedForecast: number | null;
  currentMonthUncappedForecast: number | null;
  currentMonthExhaustionDate: string | null;
}